import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { LineChart, Line, AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Flame, Heart, Footprints, Moon, Droplet, Plus, Minus, Watch, Dumbbell, Video, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";

const today = new Date().toISOString().slice(0, 10);

function Widget({ children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-5 hover:border-[#FF6B6B]/30 transition-colors ${className}`}>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [wearable, setWearable] = useState(user?.wearable || {});
  const [checklist, setChecklist] = useState({ water_glasses: 0, workouts_done: 0, reps_logged: 0, meals_done: [] });
  const [metrics, setMetrics] = useState([]);
  const [plans, setPlans] = useState([]);
  const [bookings, setBookings] = useState([]);

  const load = useCallback(async () => {
    const [w, c, m, p, b] = await Promise.all([
      api.get("/wearable"), api.get(`/checklist/${today}`), api.get("/metrics"),
      api.get("/my/plans"), api.get("/bookings"),
    ]);
    setWearable(w.data); setChecklist(c.data); setMetrics(m.data); setPlans(p.data);
    setBookings(b.data.filter((x) => x.status === "upcoming"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const connectWearable = async () => {
    const res = await api.post("/wearable/connect", { device: "Apple Watch" });
    setWearable(res.data);
    setUser({ ...user, wearable: res.data });
    toast.success("Apple Watch synced!");
  };

  const updateChecklist = async (patch) => {
    const next = { ...checklist, ...patch };
    setChecklist(next);
    await api.post("/checklist", { date: today, ...patch });
  };

  const meals = ["Breakfast", "Lunch", "Dinner", "Snack"];
  const toggleMeal = (meal) => {
    const done = checklist.meals_done || [];
    const next = done.includes(meal) ? done.filter((x) => x !== meal) : [...done, meal];
    updateChecklist({ meals_done: next });
  };

  const weightData = metrics.map((m) => ({ date: m.date?.slice(5), weight: m.weight, volume: m.volume }));

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A0A0]">My Hub</p>
          <h1 className="font-display font-bold text-4xl uppercase tracking-tight text-white">Welcome, {user?.name?.split(" ")[0]}</h1>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6B6B]/10 border border-[#FF6B6B]/30">
          <Flame className="w-5 h-5 text-[#FF6B6B]" />
          <span className="font-display font-bold text-2xl text-[#FF6B6B]">{user?.streak || 0}</span>
          <span className="text-xs uppercase tracking-wider text-[#A0A0A0]">day streak</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Wearable */}
        <Widget className="col-span-2" testid="wearable-widget">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Watch className="w-5 h-5 text-[#4ECCD3]" />
              <h3 className="font-bold text-white">Wearable Sync</h3>
            </div>
            {wearable?.connected ? (
              <span className="text-xs text-[#FF6B6B] flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FF6B6B] animate-pulse" />{wearable.device}</span>
            ) : (
              <button data-testid="connect-wearable-btn" onClick={connectWearable} className="text-xs px-3 py-1.5 rounded-full bg-[#4ECCD3] text-black font-semibold hover:brightness-110 transition-all">Connect Device</button>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3">
            {[
              { icon: Heart, label: "BPM", val: wearable?.heart_rate || "—", color: "#FF3B30" },
              { icon: Flame, label: "kcal", val: wearable?.active_calories || "—", color: "#FF6B6B" },
              { icon: Footprints, label: "steps", val: wearable?.steps || "—", color: "#4ECCD3" },
              { icon: Moon, label: "sleep", val: wearable?.sleep_hours ? `${wearable.sleep_hours}h` : "—", color: "#A0A0A0" },
            ].map((s, i) => (
              <div key={i} className="bg-[#121212] rounded-xl p-3 text-center">
                <s.icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                <p className="font-display font-bold text-xl text-white">{s.val}</p>
                <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">{s.label}</p>
              </div>
            ))}
          </div>
        </Widget>

        {/* Water */}
        <Widget testid="water-widget">
          <div className="flex items-center gap-2 mb-3"><Droplet className="w-5 h-5 text-[#4ECCD3]" /><h3 className="font-bold text-white text-sm">Water</h3></div>
          <p className="font-display font-black text-4xl text-white mb-1">{checklist.water_glasses || 0}<span className="text-base text-[#A0A0A0]">/8</span></p>
          <p className="text-xs text-[#A0A0A0] mb-3">glasses today</p>
          <div className="flex gap-2">
            <button data-testid="water-minus-btn" onClick={() => updateChecklist({ water_glasses: Math.max(0, (checklist.water_glasses || 0) - 1) })} className="flex-1 py-2 rounded-lg bg-[#2A2A2A] text-white hover:bg-[#333333] transition-colors flex justify-center"><Minus className="w-4 h-4" /></button>
            <button data-testid="water-plus-btn" onClick={() => updateChecklist({ water_glasses: (checklist.water_glasses || 0) + 1 })} className="flex-1 py-2 rounded-lg bg-[#4ECCD3] text-black hover:brightness-110 transition-all flex justify-center"><Plus className="w-4 h-4" /></button>
          </div>
        </Widget>

        {/* Reps logged */}
        <Widget testid="reps-widget">
          <div className="flex items-center gap-2 mb-3"><Dumbbell className="w-5 h-5 text-[#FF6B6B]" /><h3 className="font-bold text-white text-sm">Reps</h3></div>
          <p className="font-display font-black text-4xl text-white mb-1">{checklist.reps_logged || 0}</p>
          <p className="text-xs text-[#A0A0A0] mb-3">logged today</p>
          <div className="flex gap-2">
            <button data-testid="reps-add-btn" onClick={() => updateChecklist({ reps_logged: (checklist.reps_logged || 0) + 10 })} className="flex-1 py-2 rounded-lg bg-[#FF6B6B] text-black font-semibold text-xs hover:brightness-110 transition-all">+10 reps</button>
            <button data-testid="workout-done-btn" onClick={() => updateChecklist({ workouts_done: (checklist.workouts_done || 0) + 1 })} className="flex-1 py-2 rounded-lg bg-[#2A2A2A] text-white text-xs hover:bg-[#333333] transition-colors">+ Workout</button>
          </div>
        </Widget>

        {/* Progress overload chart */}
        <Widget className="col-span-2 lg:col-span-2" testid="volume-chart-widget">
          <h3 className="font-bold text-white mb-1">Progressive Overload</h3>
          <p className="text-xs text-[#A0A0A0] mb-4">Total training volume (lbs)</p>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={weightData}>
              <defs>
                <linearGradient id="vol" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF6B6B" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#FF6B6B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="#A0A0A0" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis hide />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid #2A2A2A", borderRadius: 8, color: "#fff" }} />
              <Area type="monotone" dataKey="volume" stroke="#FF6B6B" strokeWidth={2} fill="url(#vol)" />
            </AreaChart>
          </ResponsiveContainer>
        </Widget>

        {/* Weight chart */}
        <Widget className="col-span-2" testid="weight-chart-widget">
          <h3 className="font-bold text-white mb-1">Body Weight Trend</h3>
          <p className="text-xs text-[#A0A0A0] mb-4">Bodyweight (kg)</p>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={weightData}>
              <XAxis dataKey="date" stroke="#A0A0A0" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis hide domain={["dataMin - 1", "dataMax + 1"]} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid #2A2A2A", borderRadius: 8, color: "#fff" }} />
              <Line type="monotone" dataKey="weight" stroke="#4ECCD3" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Widget>

        {/* Daily checklist meals */}
        <Widget className="col-span-2" testid="checklist-widget">
          <h3 className="font-bold text-white mb-4">Daily Nutrition Checklist</h3>
          <div className="space-y-2">
            {meals.map((meal) => {
              const done = (checklist.meals_done || []).includes(meal);
              return (
                <button
                  key={meal}
                  data-testid={`meal-check-${meal.toLowerCase()}`}
                  onClick={() => toggleMeal(meal)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#121212] hover:bg-[#2A2A2A] transition-colors"
                >
                  <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${done ? "bg-[#FF6B6B] border-[#FF6B6B]" : "border-[#A0A0A0]"}`}>
                    {done && <span className="text-black text-xs font-bold">✓</span>}
                  </div>
                  <span className={`text-sm ${done ? "text-[#A0A0A0] line-through" : "text-white"}`}>{meal}</span>
                </button>
              );
            })}
          </div>
        </Widget>

        {/* Upcoming session */}
        <Widget className="col-span-2" testid="upcoming-session-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-white">Upcoming 1-on-1</h3>
            <button data-testid="dashboard-book-btn" onClick={() => navigate("/discover")} className="text-xs text-[#FF6B6B] flex items-center gap-1 hover:gap-2 transition-all">Book <ArrowRight className="w-3 h-3" /></button>
          </div>
          {bookings.length === 0 ? (
            <p className="text-sm text-[#A0A0A0]">No upcoming sessions. Book a coach to get started!</p>
          ) : (
            bookings.slice(0, 2).map((b) => (
              <div key={b.booking_id} className="flex items-center justify-between p-3 rounded-xl bg-[#121212] mb-2">
                <div>
                  <p className="text-sm font-semibold text-white">{b.discipline}</p>
                  <p className="text-xs text-[#A0A0A0]">with {b.instructor_name} · {new Date(b.start_time).toLocaleString()}</p>
                </div>
                <button data-testid={`join-live-${b.booking_id}`} onClick={() => navigate(`/live/${b.booking_id}`)} className="px-3 py-2 rounded-lg bg-[#FF6B6B] text-black text-xs font-semibold flex items-center gap-1 hover:brightness-110 transition-all">
                  <Video className="w-4 h-4" /> Join
                </button>
              </div>
            ))
          )}
        </Widget>

        {/* Purchased plans */}
        <Widget className="col-span-2 lg:col-span-4" testid="plans-library-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-white">My Plans Library</h3>
            <button data-testid="browse-plans-btn" onClick={() => navigate("/discover")} className="text-xs text-[#FF6B6B] flex items-center gap-1 hover:gap-2 transition-all">Browse more <ArrowRight className="w-3 h-3" /></button>
          </div>
          {plans.length === 0 ? (
            <p className="text-sm text-[#A0A0A0]">No purchased plans yet. Discover coaches and grab a program!</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {plans.map((p) => (
                <div key={p.plan_id} className="rounded-xl overflow-hidden border border-[#2A2A2A] group">
                  <div className="h-24 overflow-hidden"><img src={p.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /></div>
                  <div className="p-3">
                    <span className="text-[10px] uppercase tracking-wider text-[#FF6B6B]">{p.type}</span>
                    <p className="text-sm font-semibold text-white line-clamp-1">{p.title}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Widget>
      </div>
    </div>
  );
}
